export const REPORT_KINDS = ['GENERATED', 'ALWAYS_ON', 'DEAD'] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

export const REPORT_CATALOG_CATEGORIES = [
    'Sales',
    'Stock',
    'Purchase',
    'Transfer',
    'Wholesale',
    'Demand',
    'Compliance',
    'Finance',
    'Masters',
    'Tools',
] as const;
export type ReportCatalogCategory = (typeof REPORT_CATALOG_CATEGORIES)[number];

export const ACTIVE_STATUSES = ['Active', 'Inactive'] as const;
export type ActiveStatus = (typeof ACTIVE_STATUSES)[number];

export type ReportDateType = 'Single Date' | 'Range Date';
export type ReportFileType = 'Excel' | 'PDF';
export type SavedTimeUnit = 'minutes' | 'hours' | 'minute' | 'hour';

export interface ReportFileTypesConfig {
    isVisible: boolean;
    isSelectable: boolean;
    types: ReportFileType[];
    defaultType?: ReportFileType | null;
}

export interface ReportTimeSaved {
    value: number;
    unit: SavedTimeUnit;
}

export interface ReportDataPoint {
    name: string;
    description?: string;
}

export interface ReportConfig {
    dateType?: ReportDateType;
    fileTypes?: ReportFileTypesConfig;
    timeSaved?: ReportTimeSaved;
}

export interface CatalogListRow {
    _id: string;
    reportID: string;
    name: string;
    description?: string;
    categories: ReportCatalogCategory[];
    states: string[];
    allStates: boolean;
    kind: ReportKind;
    generatorBound: boolean;
    activeStatus: ActiveStatus;
    brandsOn: number;
    alreadyIn?: boolean;
    canAdd?: boolean;
    cannotAddReason?: 'DEAD' | 'UNBOUND' | 'INACTIVE' | 'ALWAYS_ON';
}

export interface CatalogSummary {
    canBuild: number;
    cannotBuild: number;
    alwaysOn: number;
    dead: number;
    onForZero: number;
}

export interface CatalogListResponse {
    reports: CatalogListRow[];
    total: number;
    page?: number;
    limit?: number;
    summary?: CatalogSummary;
}

export interface CatalogDetails extends CatalogListRow {
    config?: ReportConfig;
    dataPoints?: ReportDataPoint[];
    packsIn?: number;
}

export interface GeneratorOption {
    reportID: string;
    label: string;
    status: 'AVAILABLE' | 'ALREADY_USED';
    catalogReportId?: string;
}

export interface FilterOptionsResponse {
    categories: string[];
    kinds: ReportKind[];
}

export interface TemplateListRow {
    _id: string;
    name: string;
    tags: string[];
    states: string[];
    allStates: boolean;
    reportCount: number;
    activeStatus: ActiveStatus;
    alreadyIn?: boolean;
}

export interface TemplateListResponse {
    templates: TemplateListRow[];
    total: number;
    page?: number;
    limit?: number;
}

export interface TemplateReportRow {
    reportID: string;
    name: string;
    categories: ReportCatalogCategory[];
    kind: ReportKind;
    generatorBound: boolean;
    activeStatus: ActiveStatus;
}

export interface TemplateDetails {
    _id: string;
    name: string;
    tags: string[];
    states: string[];
    allStates: boolean;
    reportIds: string[];
    activeStatus: ActiveStatus;
    reports: TemplateReportRow[];
}

export interface StoreBrandListRow {
    storeBrandId: string;
    name: string;
    state?: string | null;
    mixedStates?: string[];
    alreadyOn?: boolean;
    enabledCount?: number;
    lastToggledAt?: string | null;
    wouldAdd?: boolean;
    wouldKeep?: boolean;
    wouldGain?: number;
    alreadyOnCount?: number;
}

export interface StoreBrandListResponse {
    storeBrands: StoreBrandListRow[];
    total: number;
    page?: number;
    limit?: number;
    add?: number;
    keep?: number;
    legalCount?: number;
}

export interface StoreBrandReportRow {
    reportID: string;
    name: string;
    categories: ReportCatalogCategory[];
    kind: ReportKind;
    generatorBound: boolean;
    activeStatus: ActiveStatus;
    enabled: boolean;
    availableForState: boolean;
    nameOverride?: string | null;
}

export interface StoreBrandDetails {
    brand: {
        id: string;
        name: string;
        state?: string | null;
        mixedStates?: string[];
        lastToggledAt?: string | null;
    };
    enabledCount: number;
    availableCount: number;
    whatsappPickCount: number;
    alwaysOnLine: boolean;
    reports: StoreBrandReportRow[];
}

export interface ApplyTemplateResult {
    add: number;
    keep: number;
    skippedForState: number;
    brandsPartiallySkipped: number;
    enablementsToWrite: number;
    unit: 'BRANDS' | 'REPORTS';
}

export interface UpdateTemplatesResult {
    added: number;
    removed: number;
}

export interface UpdateStoreBrandsResult {
    turnedOn: number;
    turnedOff: number;
    skipped: number;
    brandsOn: number;
    failed?: { id: string; name: string; reason: string }[];
}

export interface NameOverrideResult {
    reportID: string;
    nameOverride: string | null;
    catalogueName: string;
}

export type PlaceLoadState = 'idle' | 'loading' | 'ready' | 'error';
export type FormPhase = 'idle' | 'loading' | 'fetchError' | 'saving' | 'done';
